import { useEffect, useState } from "react";
import PouchDB from "pouchdb-browser";
import { getCouchUrl } from "@/functions/getCouchUrl";
import { logger } from "@/functions/logger";
import { attachReplicationLogging } from "@/functions/logReplication";
import { localPouchPrefix, pouches_db } from "../config";
import { useAuth } from "./useAuth";

export const syncDatabases = async (localDb, remoteDb) => {
  try {
    const db = new PouchDB(localDb);
    const db2 = new PouchDB(remoteDb);

    return await PouchDB.sync(db, db2);
  } catch (err) {
    console.log("Error while trying to sync DB: ", err);
    throw new Error(err);
  }
};

export const checkSyncStatus = async (localDb, remoteDb) => {
  try {
    const db = new PouchDB(localDb);
    const db2 = new PouchDB(remoteDb);

    const localInfo = await db.info();
    const remoteInfo = await db2.info();

    // console.log(localInfo);
    // console.log(remoteInfo);

    const update_seq_items = remoteInfo.update_seq.split("-");

    if (+localInfo.update_seq === +update_seq_items[0]) {
      // console.log("Databases are in sync");
      return true; // No pending changes
    } else {
      // console.log("Pending changes detected");
      return false; // Changes need to be replicated
    }
  } catch (error) {
    console.error("Error checking sync status:", error);
    throw new Error(error);
  }
};

const usePouchSync = (localDb, remoteDb = null, options = {}) => {
  const [syncState, setSyncState] = useState({
    syncActive: false,
    lastError: null,
    replicationStatus: null,
  });

  const { data: authData } = useAuth();

  useEffect(() => {
    if (!remoteDb) return;

    let pouchDbSyncActiveEvent = false;
    let pouchDbSyncChangeEvent = false;

    const couch_url = getCouchUrl(authData);
    const repHandler = PouchDB.sync(localDb, `${couch_url}${remoteDb}`, {
      live: true,
      retry: true,
      ...options,
    });

    // Audit-log replication health (denied/error/stalled) so silent data-loss is visible.
    attachReplicationLogging(repHandler, localDb, remoteDb);

    // Update sync state
    // setSyncState((prev) => ({ ...prev, syncActive: true }));

    // Event listeners
    repHandler
      .on("active", () => {
        pouchDbSyncActiveEvent = true;
        // setSyncState((prev) => ({
        //   ...prev,
        //   replicationStatus: "Replication active.",
        // }));
      })
      .on("change", (info) => {
        pouchDbSyncChangeEvent = true;

        // Diagnostic: a gastos sync that moves docs whose prefix != the current one
        // is the resurrection/amplifier mechanism. direction "pull" = old remote
        // refilled the local DB; "push" = local contamination flowing UP to remote.
        if (localDb === pouches_db.GASTOS) {
          try {
            const cur = localStorage.getItem(localPouchPrefix);
            const docs = info?.change?.docs ?? [];
            const foreign = docs.filter((d) => d && d.prefix && cur && d.prefix !== cur);
            if (foreign.length) {
              logger.error("gastos.foreign_replicated", "Sync moved previous-route gastos", {
                entity: "gastos",
                context: {
                  direction: info.direction,
                  remote: remoteDb,
                  current_prefix: cur,
                  foreign_count: foreign.length,
                  foreign_prefixes: [...new Set(foreign.map((d) => d.prefix))],
                  foreign_ids: foreign.slice(0, 20).map((d) => d._id),
                },
              });
            }
          } catch (e) {
            // never let logging break replication
          }
        }
      })
      .on("paused", (err) => {
        if (pouchDbSyncActiveEvent == true && pouchDbSyncChangeEvent == false) {
          // Gotcha! Syncing with remote DB not happening!
          console.log("Gotcha! Syncing with remote DB not happening!");
        } else {
          // Everything's ok. Syncing with remote DB happening normally.
        }

        pouchDbSyncActiveEvent = false;
        pouchDbSyncChangeEvent = false;

        // setSyncState((prev) => ({
        //   ...prev,
        //   replicationStatus: "Replication paused.",
        //   lastError: err || null,
        // }));
      })

      .on("error", (err) => {
        console.error("Replication error:", err);
        setSyncState((prev) => ({
          ...prev,
          syncActive: false,
          lastError: err,
        }));
      });

    // Cleanup on unmount
    return () => {
      repHandler.cancel();
      setSyncState((prev) => ({
        ...prev,
        syncActive: false,
        replicationStatus: "Replication stopped.",
      }));
    };
  }, [localDb, remoteDb]);

  return syncState;
};

export default usePouchSync;
